import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Accordion, Badge, Card, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import Moment from 'moment';
import { formatDate, formatMoney } from '../services/helpers';
import { messages as jobmessages } from '../views/Job/Job.messages';
import useI18n from "../hooks/useI18n";

const PayplanDetails = (props) => {
    const lng = props.intl.formatMessage;
    const [payplanSteps, setPayplanSteps] = useState(null);
    const [nextPayplanStep, setNextPayplanStep] = useState(null);
    const [accordionOpenKey, setAccordionOpenKey] = useState('0');
    const { getLocale } = useI18n();
    const userLocale = getLocale();

    useEffect(() => {

        if (props.job.payplan !== null) {

            let allPaid = true;
            setPayplanSteps(props.job.payplan);

            // also catch nearest unpaid payplan step
            for (const step of props.job.payplan) {
                if (!step.ispaid) {
                    allPaid = false;
                    setNextPayplanStep(step);
                    break;
                }
            }

            // if all are paid, open accordion
            if (allPaid) {
                setAccordionOpenKey('1');
            }
        } else {
            setPayplanSteps(null);
        }

    }, [props.job]);


    // payplan steps 
    return (
        <Accordion flush defaultActiveKey={accordionOpenKey}>
            <Accordion.Item eventKey="1">
                <Accordion.Header aria-label={lng(jobmessages.showPaymentPlan)}>
                    <Col >
                        <span className='label'>
                            {lng(jobmessages.payplan)}
                        </span>
                    </Col>
                    <Col className='float-end text-end pe-2 pe-lg-3'>
                        {nextPayplanStep !== null && (
                            nextPayplanStep.isPending ? (
                                <p className='mb-0'>
                                    {formatMoney(nextPayplanStep.amount, userLocale, props.job.currency)} {lng(jobmessages.pending)}
                                </p>
                            ) : (
                                <p className='mb-0'> {formatMoney(nextPayplanStep.amount, userLocale, props.job.currency)} {lng(jobmessages.paymentIsDue2)}
                                    {Moment(nextPayplanStep.paydate).format("YYYY-MM-DD") <= Moment().format("YYYY-MM-DD") ?
                                        (<span className='text-danger'> {formatDate(nextPayplanStep.paydate)} <FontAwesomeIcon icon={far.faCircleExclamation} /></span>) :
                                        (<span> {formatDate(nextPayplanStep.paydate)}</span>)
                                    }
                                </p>
                            )
                        )}
                    </Col>
                </Accordion.Header>
                <Accordion.Body id='payplan-steps'>
                    {payplanSteps !== null && payplanSteps.map((step, index) => {
                        // is it due today
                        const isDueNow = Moment(step.paydate).format("YYYY-MM-DD") === Moment().format("YYYY-MM-DD");

                        // is it already due
                        const isDue = Moment(step.paydate).format("x") < Moment().format("x");

                        // is it paid
                        const isPaid = step.diff <= 0;
                        
                        // is payplan step pending
                        const isPending = !isPaid && step.isPending;
                        
                        let rowClasses = 'step ';
                        let rowStatusText = false;

                        if (isPaid) {
                            rowClasses += 'paid';
                            rowStatusText = <span id='paid-label'>{lng(jobmessages.paid)}</span>;
                        }
                        else if (isPending) {
                            rowClasses += 'is-pending';
                            rowStatusText = <span id='due-label-pending'>{lng(jobmessages.pending)}</span>;
                        }
                        else if (isDueNow) {
                            rowClasses += 'is-due';
                            rowStatusText = <span id='due-label-due-now'>{lng(jobmessages.dueNow)}</span>;
                        }
                        else if (isDue) {
                            rowClasses += 'is-due';
                            rowStatusText = <span id='due-label-due'>{lng(jobmessages.due)}</span>;
                        }

                        return (
                            <OverlayTrigger
                                key={'step-' + index}
                                trigger={['hover', 'click']}
                                overlay={rowStatusText ? (
                                    <Tooltip>{rowStatusText}</Tooltip>
                                ) : (<></>)}
                            >
                                <Card className={'payplan-step ' + rowClasses}>
                                    <Row>
                                        <Col xs={4} lg={4} className='text-end'>
                                            <p className='due-date '>
                                                <span className='label text-muted d-block'>{lng(jobmessages.paydate)}</span>
                                                <span className='date'>{formatDate(step.paydate)}</span>
                                                <span className='payplan-status d-none d-xl-block'>{rowStatusText}</span>
                                            </p>
                                        </Col>
                                        <Col xs={4} lg={4} className='text-end'>
                                            <p className='amount'>
                                                <span className='label text-muted d-block'>{lng(jobmessages.sum)}</span>
                                                {formatMoney(step.amount, userLocale, props.job.currency)}
                                            </p>
                                        </Col>
                                        <Col xs={4} lg={4} className='text-end'>

                                            {/* If paid, do not show openAmount */}
                                            {isPaid ? ('') : (
                                                <p className='open-amount'>
                                                    <span className='label text-muted d-block'>{lng(jobmessages.openAmount)}</span>
                                                    <strong>{formatMoney(step.diff, userLocale, props.job.currency)}</strong>
                                                    {step.paid > 0 &&
                                                        <div><Badge pill bg="success">{lng(jobmessages.paid)} {formatMoney(step.paid, userLocale, props.job.currency)}</Badge></div>
                                                    }
                                                </p>
                                            )}

                                            {/* If paid value < 0 and not fully paid, don't show paid amount */}
                                            {step.paid <= 0 || !isPaid ? ('') : (
                                                <p className='paid'>
                                                    <span className='label text-muted d-block'>{lng(jobmessages.paid)}</span>
                                                    <strong>{formatMoney(step.paid, userLocale, props.job.currency)}</strong>
                                                </p>
                                            )}

                                        </Col>
                                        <Badge className={rowClasses} pill>{index + 1}</Badge>
                                    </Row>
                                </Card>
                            </OverlayTrigger>
                        );
                    })
                    }
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

PayplanDetails.propTypes = {
    'intl': PropTypes.object.isRequired,
    'job': PropTypes.object
};

export default PayplanDetails;